interface RevenueDataPoint {
  month: string;
  revenue: number;
  collected: number;
}

interface RevenueChartProps {
  data: RevenueDataPoint[];
}

export default function RevenueChart({ data }: RevenueChartProps) {
  const maxVal = Math.max(...data.map((d) => d.revenue));
  const chartH = 160;
  const chartW = 560;
  const padL = 48;
  const padR = 16;
  const padT = 16;
  const padB = 32;
  const innerW = chartW - padL - padR;
  const innerH = chartH - padT - padB;
  const n = data.length;
  const barGroupW = innerW / n;
  const barW = barGroupW * 0.32;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
    val: t * maxVal,
    y: padT + innerH - t * innerH,
  }));

  const revenuePoints = data.map((d, i) => {
    const x = padL + i * barGroupW + barGroupW / 2;
    const y = padT + innerH - (d.revenue / maxVal) * innerH;
    return `${x},${y}`;
  }).join(" ");

  const collectedPoints = data.map((d, i) => {
    const x = padL + i * barGroupW + barGroupW / 2;
    const y = padT + innerH - (d.collected / maxVal) * innerH;
    return `${x},${y}`;
  }).join(" ");

  const formatVal = (v: number) => v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : `₹${(v / 1000).toFixed(0)}K`;

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full" style={{ minWidth: 320 }}>
        {/* Grid lines */}
        {yTicks.map((t) => (
          <g key={t.val}>
            <line x1={padL} y1={t.y} x2={chartW - padR} y2={t.y} stroke="#f1f5f9" strokeWidth="1" />
            <text x={padL - 6} y={t.y + 4} textAnchor="end" fontSize="9" fill="#94a3b8">{formatVal(t.val)}</text>
          </g>
        ))}

        {/* Bars */}
        {data.map((d, i) => {
          const groupX = padL + i * barGroupW;
          const revH = (d.revenue / maxVal) * innerH;
          const colH = (d.collected / maxVal) * innerH;
          return (
            <g key={d.month}>
              <rect
                x={groupX + barGroupW * 0.12}
                y={padT + innerH - revH}
                width={barW}
                height={revH}
                rx="3"
                fill="#f59e0b"
                opacity="0.85"
              />
              <rect
                x={groupX + barGroupW * 0.12 + barW + 3}
                y={padT + innerH - colH}
                width={barW}
                height={colH}
                rx="3"
                fill="#fcd34d"
                opacity="0.7"
              />
              <text
                x={groupX + barGroupW / 2}
                y={padT + innerH + 14}
                textAnchor="middle"
                fontSize="9"
                fill="#94a3b8"
              >
                {d.month}
              </text>
            </g>
          );
        })}

        {/* Trend line - revenue */}
        <polyline
          points={revenuePoints}
          fill="none"
          stroke="#f59e0b"
          strokeWidth="1.5"
          strokeDasharray="4 2"
          opacity="0.5"
        />
        <polyline
          points={collectedPoints}
          fill="none"
          stroke="#fbbf24"
          strokeWidth="1.5"
          strokeDasharray="4 2"
          opacity="0.5"
        />

        {/* Dots */}
        {data.map((d, i) => {
          const x = padL + i * barGroupW + barGroupW / 2;
          const ry = padT + innerH - (d.revenue / maxVal) * innerH;
          const cy = padT + innerH - (d.collected / maxVal) * innerH;
          return (
            <g key={`dot-${d.month}`}>
              <circle cx={x} cy={ry} r="3" fill="#f59e0b" />
              <circle cx={x} cy={cy} r="3" fill="#fbbf24" />
            </g>
          );
        })}
      </svg>
    </div>
  );
}
